import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { ICustomPageToolbar } from '@se/cp.common';
import { ButtonsTypes } from 'estensa-module';
import { each, values } from 'lodash-es';
import { BehaviorSubject, take } from 'rxjs';

import { EWhere } from '../../services/tp-integrations/tp-integrations.interface';
import { CustomPageType } from '../../custom-pages/cp-enum';
import { seSetToolbarInfo } from '../../root-store/root.actions';
import { selectSeSearchSliders } from '../../root-store/root.selectors';
import { RootState } from '../../root-store/root.state';
import { SearchPageConfigurationService } from '../../search-page/search-page-configuration.service';
import { EstensaFormType, SearchFormType } from '../../search-page/search-page-type.enum';
import { ButtonsService } from '../../services/angularjs/estensa-buttons.service';
import { TpIntegrationsService } from '../../services/tp-integrations/tp-integrations.service';
import { seToolbarInitialize } from './redux/se-buttons-toolbar.actions';
import { SLIDER_OPTIONS, TIMELINE_OPTIONS } from './redux/se-buttons-toolbar.state';
import { mkButtonData, SlidersData, ToolbarAction, ToolbarButtonData } from './types';

type SlidersKey = CustomPageType | SearchFormType | EstensaFormType;

@Injectable({
    providedIn: 'root',
})
export class SeButtonsToolbarService {
    leftSliderStatus$ = this.store.select('buttonsToolbar', 'leftSlider');

    rightSliderStatus$ = this.store.select('buttonsToolbar', 'rightSlider');

    timelineStatus$ = this.store.select('buttonsToolbar', 'timeline');

    get currentButtonsData$() {
        return this.currentButtonsDataSubject.asObservable();
    }

    private slidersInitialData: Partial<Record<SlidersKey, SlidersData>> = {};

    private buttonsData: Partial<Record<SlidersKey, ToolbarButtonData[]>> = {};

    private currentButtonsDataSubject = new BehaviorSubject<ToolbarButtonData[]>([]);

    constructor(
        private actions: Actions,
        private buttonsService: ButtonsService,
        private store: Store<RootState>,
        private searchPageConfigurationService: SearchPageConfigurationService,
        private tpIntegrations: TpIntegrationsService,
    ) {
        SeButtonsToolbarService.checkValueConsistency();

        this.initSearchPagesButtonsData();
        this.initCustomPagesButtonsData();
        this.initEstensaPagesButtonsData();
        this.initThirdPartButtonsData();

        this.initSliderData();
        this.initAuthorizations();

        this.actions.pipe(ofType(seToolbarInitialize)).subscribe((action) => {
            this.currentButtonsDataSubject.next(action.buttons);
        });
    }

    private initThirdPartButtonsData() {
        /*creare action che aggiungano questi buttons direttamente nello store*/

        this.buttonsData[CustomPageType.CARDIOLOGY_VISIT] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[CustomPageType.CARDIOLOGY_VISIT] || [],
            EWhere.CUSTOM_PAGE_CARDIOLOGY_VISIT,
        );

        this.buttonsData[CustomPageType.TELECONSULTATION] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[CustomPageType.TELECONSULTATION] || [],
            EWhere.CUSTOM_PAGE_TELECONSULTATION,
        );

        this.buttonsData[CustomPageType.TELEMONITORING_VISIT] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[CustomPageType.TELEMONITORING_VISIT] || [],
            EWhere.CUSTOM_PAGE_TELEMONITORING_VISIT,
        );

        this.buttonsData[CustomPageType.ULTRASOUND] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[CustomPageType.ULTRASOUND] || [],
            EWhere.CUSTOM_PAGE_ULTRASOUND,
        );

        this.buttonsData[SearchFormType.VISIT] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.VISIT] || [],
            EWhere.LIST_PAGE_VISIT,
        );

        this.buttonsData[SearchFormType.ECG] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.ECG] || [],
            EWhere.LIST_PAGE_ECG,
        );

        this.buttonsData[SearchFormType.COMPLETE_CD] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.COMPLETE_CD] || [],
            EWhere.LIST_PAGE_COMPLETE_CD,
        );

        this.buttonsData[SearchFormType.COLLABORATION] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.COLLABORATION] || [],
            EWhere.LIST_PAGE_COLLABORATION,
        );

        this.buttonsData[SearchFormType.ON_PRINTING_CD] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.ON_PRINTING_CD] || [],
            EWhere.LIST_PAGE_ON_PRINTING_CD,
        );

        this.buttonsData[SearchFormType.TELEMONITORING] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.TELEMONITORING] || [],
            EWhere.LIST_PAGE_TELEMONITORING,
        );

        this.buttonsData[SearchFormType.NRE] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.NRE] || [],
            EWhere.LIST_PAGE_NRE,
        );

        this.buttonsData[SearchFormType.SPECIALTY] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.SPECIALTY] || [],
            EWhere.LIST_PAGE_NRE,
        );
    }

    private initAuthorizations() {
        each(this.buttonsData, (buttons, key, buttonsData) => {
            if (buttonsData[key as SlidersKey])
                buttonsData[key as SlidersKey] =
                    buttons?.filter((button) => this.buttonsService.isButtonTypeAuthorized(button.permission)) ?? [];
        });
    }

    /// Assegna i valori di default ovvero di partenza degli slider per le singole pagine
    private initSliderData() {
        // Pagine Generiche
        this.slidersInitialData[EstensaFormType.ECG_REPORTING] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OVERLAP_CLOSED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };
        this.slidersInitialData[EstensaFormType.TELECONSULTATION] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        // Search Pages

        /* ECG  */
        this.slidersInitialData[SearchFormType.ECG] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_CLOSED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED, // TODO_DGS implementare quando saranno disponibili eventuali settings
        };

        // Applico il settings sullo stato iniziale ECG del pannello
        if (
            this.searchPageConfigurationService.getIsRightPanelVisibleOnStart(SearchFormType.ECG) &&
            !!this.slidersInitialData[SearchFormType.ECG]
        )
            (this.slidersInitialData[SearchFormType.ECG] as SlidersData).rightSlider = SLIDER_OPTIONS.RIGHT_SLIDER_OPEN;

        /* VISIT  */
        this.slidersInitialData[SearchFormType.VISIT] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_CLOSED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED, // TODO_DGS implementare quando saranno disponibili eventuali settings
        };

        // Applico il settings sullo stato iniziale VISIT del pannello
        if (
            this.searchPageConfigurationService.getIsRightPanelVisibleOnStart(SearchFormType.VISIT) &&
            this.slidersInitialData[SearchFormType.VISIT]
        )
            (this.slidersInitialData[SearchFormType.VISIT] as SlidersData).rightSlider =
                SLIDER_OPTIONS.RIGHT_SLIDER_OPEN;

        /* COMPLETE_CD  */
        this.slidersInitialData[SearchFormType.COMPLETE_CD] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* COLLABORATION  */
        this.slidersInitialData[SearchFormType.COLLABORATION] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* ON_PRINTING_CD  */
        this.slidersInitialData[SearchFormType.ON_PRINTING_CD] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* TELEMONITORING  */
        this.slidersInitialData[SearchFormType.TELEMONITORING] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* NRE  */
        this.slidersInitialData[SearchFormType.NRE] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        //TOOLUPLOAD
        this.slidersInitialData[SearchFormType.TOOLUPLOAD] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* DUMMY_VNEXT2 - DEBUG  */
        this.slidersInitialData[SearchFormType.DUMMY_VNEXT2] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_CLOSED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* EXECUTION  */
        this.slidersInitialData[SearchFormType.EXECUTION] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_CLOSED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        // Applico il settings sullo stato iniziale EXECUTION del pannello
        if (
            this.searchPageConfigurationService.getIsRightPanelVisibleOnStart(SearchFormType.EXECUTION) &&
            this.slidersInitialData[SearchFormType.EXECUTION]
        )
            (this.slidersInitialData[SearchFormType.EXECUTION] as SlidersData).rightSlider =
                SLIDER_OPTIONS.RIGHT_SLIDER_OPEN;

        /* CUSTOM PAGES  */
        this.slidersInitialData[CustomPageType.CARDIOLOGY_VISIT] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
        };

        this.slidersInitialData[CustomPageType.TELECONSULTATION] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
        };

        this.slidersInitialData[CustomPageType.TELEMONITORING_VISIT] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
        };

        this.slidersInitialData[CustomPageType.ULTRASOUND] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
        };
    }

    // Inizializza la prima volta i pulsanti e lo stato delle slider per le search page
    initSearchPageToolbar(pageType: SearchFormType) {
        if (!!this.slidersInitialData[pageType] && !(this.slidersInitialData[pageType] as SlidersData).initialized) {
            (this.slidersInitialData[pageType] as SlidersData).initialized = true;

            this.store.dispatch(
                seToolbarInitialize({
                    buttons: this.getButtonsDataList(pageType),
                    leftSlider: (this.slidersInitialData[pageType] as SlidersData).leftSlider,
                    rightSlider: (this.slidersInitialData[pageType] as SlidersData).rightSlider,
                    timeline: (this.slidersInitialData[pageType] as SlidersData).timeline ?? TIMELINE_OPTIONS.TIMELINE_DISABLED,
                }),
            );
        } else {
            // Dal secondo giro in poi prendo lo stato salvato della pagina
            this.store
                .select(selectSeSearchSliders(pageType))
                .pipe(take(1))
                .subscribe((state) => {
                    this.store.dispatch(
                        seToolbarInitialize({
                            buttons: this.getButtonsDataList(pageType),
                            leftSlider: state.left,
                            rightSlider: state.right,
                            timeline: state.timeline,
                        }),
                    );
                });
        }
    }

    // Inizializza la prima volta i pulsanti e lo stato delle slider per le custom page
    initCustomPageToolbar(cpToolbar: ICustomPageToolbar) {
        const sliders = this.slidersInitialData[cpToolbar.config.type as SlidersKey];
        if (!sliders) {
            return;
        }

        if (!sliders.initialized) {
            this.store.dispatch(
                seSetToolbarInfo({
                    info1: cpToolbar.info1,
                    info2: cpToolbar.info2,
                }),
            );

            this.store.dispatch(
                seToolbarInitialize({
                    buttons: this.getButtonsDataList(cpToolbar.config.type),
                    leftSlider: cpToolbar.config.leftSliderConfig,
                    rightSlider: cpToolbar.config.rightSliderConfig,
                    timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
                }),
            );
        }
    }

    // Inizializza la prima volta i pulsanti e lo stato delle slider per pagine interne
    initEstensaPageToolbar(estensaPageType: EstensaFormType) {
        if (
            !!this.slidersInitialData[estensaPageType] &&
            !(this.slidersInitialData[estensaPageType] as SlidersData).initialized
        ) {
            this.store.dispatch(
                seToolbarInitialize({
                    buttons: this.getButtonsDataList(estensaPageType),
                    leftSlider: (this.slidersInitialData[estensaPageType] as SlidersData).leftSlider,
                    rightSlider: (this.slidersInitialData[estensaPageType] as SlidersData).rightSlider,
                    timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
                }),
            );
        }
    }

    // #region Private Methods

    private initCustomPagesButtonsData() {
        // Pagina interna - CUSTOM PAGE
        this.buttonsData[CustomPageType.CARDIOLOGY_VISIT] = [
            mkButtonData(ToolbarAction.SAVE, ButtonsTypes.saveReport),
            //mkButtonData(ToolbarAction.COLLABORATION_TEAMS, ButtonsTypes.openCollaborationForm),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport),
            mkButtonData(ToolbarAction.PATIENT, ButtonsTypes.patient),
            mkButtonData(ToolbarAction.IMPORT_VISIT, ButtonsTypes.importVisit),
            mkButtonData(ToolbarAction.APPLY_NORMAL_VALUES, ButtonsTypes.applyNormalValues),
            mkButtonData(ToolbarAction.PRINT_CERTIFICATE, ButtonsTypes.printCertificate),
            mkButtonData(ToolbarAction.IMAGES, ButtonsTypes.images),
            mkButtonData(ToolbarAction.BURN, ButtonsTypes.burn),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs),
            mkButtonData(ToolbarAction.OBSCURED_DATA, ButtonsTypes.obscuredData),
            mkButtonData(ToolbarAction.VISIT_TYPE, ButtonsTypes.visitType),
            mkButtonData(ToolbarAction.FORCE_VISIT_TYPE, ButtonsTypes.forceVisitType),
            //mkButtonData(ToolbarAction.DELETE_EXAMINATION, ButtonsTypes.deleteReportExamination), //98622 rimuovere definitivamente quando sara tutto testato
            mkButtonData(ToolbarAction.CREATE_MEDICAL_PRESCRIPTION, ButtonsTypes.createMedicalPrescription),
            mkButtonData(ToolbarAction.IMPORT_INTO_VNA, ButtonsTypes.importVna),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
        ];

        this.buttonsData[CustomPageType.TELECONSULTATION] = [
            mkButtonData(ToolbarAction.TELECONSULTATION_SAVE, ButtonsTypes.editTeleconsultation),
            mkButtonData(ToolbarAction.TELECONSULTATION_APPROVE, ButtonsTypes.editTeleconsultation),
        ];

        this.buttonsData[CustomPageType.TELEMONITORING_VISIT] = [
            mkButtonData(ToolbarAction.SAVE, ButtonsTypes.saveReport),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport),
            mkButtonData(ToolbarAction.PATIENT, ButtonsTypes.patient),
            mkButtonData(ToolbarAction.IMPORT_VISIT, ButtonsTypes.importVisit),
            mkButtonData(ToolbarAction.APPLY_NORMAL_VALUES, ButtonsTypes.applyNormalValues),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs),
            mkButtonData(ToolbarAction.OBSCURED_DATA, ButtonsTypes.obscuredData),
        ];

        this.buttonsData[CustomPageType.ULTRASOUND] = [
            mkButtonData(ToolbarAction.SAVE, ButtonsTypes.saveReport),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport),
            mkButtonData(ToolbarAction.PATIENT, ButtonsTypes.patient),
            mkButtonData(ToolbarAction.IMPORT_VISIT, ButtonsTypes.importVisit),
            mkButtonData(ToolbarAction.APPLY_NORMAL_VALUES, ButtonsTypes.applyNormalValues),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs),
            mkButtonData(ToolbarAction.OBSCURED_DATA, ButtonsTypes.obscuredData),
            mkButtonData(ToolbarAction.VISIT_TYPE, ButtonsTypes.visitType),
            mkButtonData(ToolbarAction.FORCE_VISIT_TYPE, ButtonsTypes.forceVisitType),
            mkButtonData(ToolbarAction.IMAGES, ButtonsTypes.images),
            mkButtonData(ToolbarAction.DELETE_EXAMINATION, ButtonsTypes.deleteReportExamination),
            mkButtonData(ToolbarAction.IMPORT_INTO_VNA, ButtonsTypes.importVna),
            mkButtonData(ToolbarAction.COLLABORATION_TEAMS, ButtonsTypes.openCollaborationForm),
            mkButtonData(ToolbarAction.BURN, ButtonsTypes.burn, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
        ];

    }

    private initSearchPagesButtonsData() {
        // LISTE

        //SPECIALTY
        this.buttonsData[SearchFormType.SPECIALTY] = [
            mkButtonData(ToolbarAction.OPEN, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ADD_EXAM, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.DELETE_EXAMINATION, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(
                {
                    action: ToolbarAction.EXAM,
                    label: 'ToolbarModifyExams',
                },
                ButtonsTypes.assignExam,
                { visible: true, disabled: true },
                [
                    {
                        actionType: ToolbarAction.ADD_EXAM_TO_REPORT,
                        label: ToolbarAction.ADD_EXAM_TO_REPORT,
                        isToggle: false,
                        permission: ButtonsTypes.assignExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.REMOVE_EXAM,
                        label: ToolbarAction.REMOVE_EXAM,
                        isToggle: false,
                        permission: ButtonsTypes.deleteExamination,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ANNULMENT, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.BURN, ButtonsTypes.printReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.COLLABORATION_TEAMS, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
        ];

        this.buttonsData[SearchFormType.VISIT] = [
            mkButtonData(ToolbarAction.OPEN, ButtonsTypes.editReport, { visible: true, disabled: true }),

            mkButtonData(
                {
                    action: ToolbarAction.EXAM,
                    label: 'ToolbarModifyExams',
                },
                ButtonsTypes.assignExam,
                { visible: true, disabled: true },
                [
                    {
                        actionType: ToolbarAction.ADD_EXAM_TO_REPORT,
                        label: ToolbarAction.ADD_EXAM_TO_REPORT,
                        isToggle: false,
                        permission: ButtonsTypes.assignExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.REMOVE_EXAM,
                        label: ToolbarAction.REMOVE_EXAM,
                        isToggle: false,
                        permission: ButtonsTypes.deleteExamination,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),
            mkButtonData(ToolbarAction.DELETE_EXAMINATION, ButtonsTypes.deleteReportExamination),
            mkButtonData(
                {
                    action: ToolbarAction.MERGE_EXAMS,
                    label: 'Merging',
                },
                ButtonsTypes.mergeExams,
                { visible: true, disabled: false },
                [
                    {
                        actionType: ToolbarAction.MERGE_EXAMS,
                        label: 'ToolbarMergeExams',
                        isToggle: false,
                        permission: ButtonsTypes.mergeExams,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.UNDO_MERGE_EXAMS,
                        label: 'ToolbarUndoMergeExams',
                        isToggle: false,
                        permission: ButtonsTypes.undoMergeExams,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),

            mkButtonData(ToolbarAction.CONNECT_PATIENT, ButtonsTypes.mergePatient, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.DISCONNECT_PATIENT, ButtonsTypes.undoMergePatient, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.NEW_EXAM, ButtonsTypes.saveAdmission, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ADD_EXAM, ButtonsTypes.addExam, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.BURN, ButtonsTypes.burn),

            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ANNULMENT, ButtonsTypes.annulmentReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.CREATE_MEDICAL_PRESCRIPTION, ButtonsTypes.createMedicalPrescription, {
                visible: false,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
            // mkButtonData(ToolbarAction.PRINT_CERTIFICATE, ButtonsTypes.printCertificate),
            mkButtonData(ToolbarAction.COLLABORATION_TEAMS, ButtonsTypes.openCollaborationForm, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.SUSPEND, ButtonsTypes.suspend, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_SUSPENSION, ButtonsTypes.undoSuspension, { visible: true, disabled: true }),
        ];

        this.buttonsData[SearchFormType.ECG] = [
            mkButtonData(ToolbarAction.OPEN, ButtonsTypes.editReport, { visible: true, disabled: true }),
            //mkButtonData(ToolbarAction.COLLABORATION_TEAMS, ButtonsTypes.openCollaborationForm, { active: true, disabled: true }),
            mkButtonData(ToolbarAction.REPORT_WITHOUT_TRACE, ButtonsTypes.reportWithoutTrace, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.MERGE_EXAMS, ButtonsTypes.mergeExams, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_MERGE_EXAMS, ButtonsTypes.undoMergeExams, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.CONNECT_PATIENT, ButtonsTypes.mergePatient, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.DISCONNECT_PATIENT, ButtonsTypes.undoMergePatient, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.ADD_EXAM, ButtonsTypes.addExam, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ANNULMENT, ButtonsTypes.annulmentReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
            mkButtonData(ToolbarAction.SUSPEND, ButtonsTypes.suspend, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_SUSPENSION, ButtonsTypes.undoSuspension, { visible: true, disabled: true }),
        ];

        this.buttonsData[SearchFormType.COMPLETE_CD] = [
            mkButtonData(ToolbarAction.COPY, ButtonsTypes.copyCD, { visible: true, disabled: true }),
        ];

        this.buttonsData[SearchFormType.COLLABORATION] = [
            // mkButtonData(ToolbarAction.ACTIVATE_COLLABORATION, ButtonsTypes.activateCollaboration),
        ];

        this.buttonsData[SearchFormType.ON_PRINTING_CD] = [
            mkButtonData(ToolbarAction.UNLOCK_MPS_CD, ButtonsTypes.unlockCD, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.DELETE, ButtonsTypes.deleteCD, { visible: true, disabled: true }),
        ];

        this.buttonsData[SearchFormType.TELEMONITORING] = [
            mkButtonData(ToolbarAction.OPEN_DIARY, ButtonsTypes.openDiary, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.PATIENT_ENROLMENT, ButtonsTypes.patientEnrolment, {
                visible: true,
                disabled: false,
            }),
            mkButtonData(ToolbarAction.CREATE_REMOTE_VISIT, ButtonsTypes.createRemoteVisit, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.MONITORING_KIT_ASSOCIATION, ButtonsTypes.monitoringKitAssociation, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.PATIENT_CARD_AND_ALARMS, ButtonsTypes.patientCardAndAlarms, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.PERSONAL_FOLDER, ButtonsTypes.personalFolder, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.COMMUNICATION, ButtonsTypes.communication, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.PATIENT_DOCUMENT, ButtonsTypes.patientDocument, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.COMPILE_NOTE, ButtonsTypes.compileNote, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs, { visible: true, disabled: true }),
        ];

        this.buttonsData[SearchFormType.NRE] = [
            mkButtonData(ToolbarAction.CREATE_MEDICAL_PRESCRIPTION, ButtonsTypes.createMedicalPrescription, {
                visible: true,
                disabled: false,
            }),
        ];

        this.buttonsData[SearchFormType.DUMMY_VNEXT2] = [
            mkButtonData(ToolbarAction.DUMMY_VNEXT2_TEST_ACTION, ButtonsTypes.dummyVnex2TestAction, {
                visible: true,
                disabled: false,
            }),
        ];

        this.buttonsData[SearchFormType.EXECUTION] = [
            mkButtonData(ToolbarAction.ADMISSION, ButtonsTypes.directAdmission, { visible: true, disabled: true }),
            mkButtonData(
                {
                    action: ToolbarAction.PATIENT,
                    label: 'Patient',
                },
                ButtonsTypes.patient,
                { visible: true, disabled: true },
                [
                    {
                        actionType: ToolbarAction.PATIENT_REGISTRY,
                        label: 'MasterData',
                        isToggle: false,
                        permission: ButtonsTypes.patientRegistry,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.RADIOLOGY_FOLDER,
                        label: 'RadiologyFolder',
                        isToggle: false,
                        permission: ButtonsTypes.patientFolder,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),
            mkButtonData(ToolbarAction.ADMINISTRATION_DASHBOARD, ButtonsTypes.dashboard, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.FEA_AUTH_MANAGEMENT, ButtonsTypes.feaAuthorizationMngt, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(
                {
                    action: ToolbarAction.EXAM_MANAGEMENT,
                    label: 'ExamManagement',
                },
                ButtonsTypes.examManagement,
                { visible: true, disabled: true },
                [
                    {
                        actionType: ToolbarAction.EDIT_EXAM, // modifica esame
                        label: 'ModifyExam',
                        isToggle: false,
                        permission: ButtonsTypes.editExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.ADD_EXAM,
                        label: 'AddExam',
                        isToggle: false,
                        permission: ButtonsTypes.addExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.EXE_CANCEL,
                        label: 'DeleteExamination',
                        isToggle: false,
                        permission: ButtonsTypes.deleteExamination,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.EDIT_EXAMINATION, // modifica dettagli esame
                        label: 'ModifyExamination',
                        isToggle: false,
                        permission: ButtonsTypes.editExamination,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),
            mkButtonData(ToolbarAction.SEND_TO_WORKLIST, ButtonsTypes.sendToWorkList, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXE_EXECUTE_AS_LOGGED_USER, ButtonsTypes.exeExecuteAsLoggedUser, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXE_EXECUTE_OTHER, ButtonsTypes.exeExecuteOther, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXE_UNDO_EXECUTION, ButtonsTypes.exeUndoExecution, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXE_SUSPEND, ButtonsTypes.exeSuspend, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_SUSPENSION, ButtonsTypes.undoSuspension, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNLOAD, ButtonsTypes.unload, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.VIEW_IMAGES, ButtonsTypes.images, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ENABLE_WEB_DELIVERY, ButtonsTypes.enableWebDelivery, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.DISABLE_WEB_DELIVERY, ButtonsTypes.disableWebDelivery, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.VIEW_ANAMNESTIC_DATA, ButtonsTypes.viewAnamnesticData, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(
                {
                    action: ToolbarAction.PRINTS,
                    label: 'Prints',
                },
                ButtonsTypes.print,
                { visible: true, disabled: true },
                [
                    {
                        actionType: ToolbarAction.PRINT_LABELS,
                        label: 'PrintLabel',
                        isToggle: false,
                        permission: ButtonsTypes.printLabels,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.PRINT_LIST,
                        label: 'PrintList',
                        isToggle: false,
                        permission: ButtonsTypes.printList,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.PRINT_MODULES,
                        label: 'PrintModules',
                        isToggle: false,
                        permission: ButtonsTypes.printModules,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.PRINT_REPORT,
                        label: 'PrintReport',
                        isToggle: false,
                        permission: ButtonsTypes.printReport,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.SEND_TO_REMOTE_REPORTING, ButtonsTypes.sendToRemoteReporting, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.CREATE_TELECONSULTATION, ButtonsTypes.newTeleconsultation, {
                visible: true,
                disabled: true,
            }),
            // mkButtonData(ToolbarAction.EXE_CANCEL, ButtonsTypes.exeCancel, { active: true, disabled: true }),
            /*mkButtonData(ToolbarAction.EXE_CHANGE_TECHNICIAN, ButtonsTypes.exeChangeTechnician, { active: true, disabled: true })*/
        ];
    }

    private initEstensaPagesButtonsData() {
        this.buttonsData[EstensaFormType.ECG_REPORTING] = [
            mkButtonData(ToolbarAction.SAVE, ButtonsTypes.editReport, { visible: true, disabled: true }),
            //mkButtonData(ToolbarAction.COLLABORATION_TEAMS, ButtonsTypes.openCollaborationForm, { active: true, disabled: true }),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.DEFINITIVE_AND_PRINT, ButtonsTypes.approveReportAndPrint, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.PRINT, ButtonsTypes.printReport, { visible: true, disabled: true }),
        ];

        this.buttonsData[EstensaFormType.TELECONSULTATION] = [];
    }

    /**
     * Internal toggle toolbar button of se buttons toolbar service
     */
    private static internalToggleToolbarButton(action: ToolbarAction, list: ToolbarButtonData[]) {
        for (let btn of list) {
            if (btn.actionType == action) {
                btn.status.checked = !btn.status.checked;

                return btn.status.checked;
            } else if (btn.children && btn.children.length > 0) {
                // Eventuale sub menu
                for (let subBtn of btn.children) {
                    if (subBtn.actionType == action) {
                        subBtn.status.checked = !subBtn.status.checked;

                        return subBtn.status.checked;
                    }
                }
            }
        }

        return undefined;
    }

    private static checkValueConsistency() {
        const allButtonTypes = values(ToolbarAction);
        const tmp = allButtonTypes.filter((el, i, a) => i === a.indexOf(el));

        if (tmp.length !== allButtonTypes.length) {
            alert(
                'Ci sono dei valori duplicati nell\'enumerato ToolbarActionType correggere in se-buttons-toolbar.actions.ts',
            );
        }
    }

    // #endregion Private Methods

    // #region Public Methods

    public getButtonsDataList(pageType: SlidersKey): ToolbarButtonData[] {
        return this.buttonsData[pageType] ?? [];
    }

    public static toggleToolbarButton(
        action: ToolbarAction,
        buttonsList: ToolbarButtonData[],
        hideButtonsList?: ToolbarButtonData[],
    ) {
        let toggled = SeButtonsToolbarService.internalToggleToolbarButton(action, buttonsList);
        if (toggled == undefined)
            toggled = SeButtonsToolbarService.internalToggleToolbarButton(action, hideButtonsList ?? []);

        return toggled;
    }

    // #endregion
}
